import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { hideLoader, showLoader } from '@utils/Loader';
import ModalNewTemplate from '@screens/Notifications/ModalNewTemplate';

/**
 * Todas las plantillas de WhatsApp de la cuenta, con su estado de Meta.
 *
 * ⚠️ **Aquí NO se editan textos, y no es una limitación nuestra**: una plantilla
 * aprobada por Meta es inmutable. Lo que se puede hacer es **crear** una nueva
 * (que entra en revisión) y **borrar** las que ya no sirven. Cambiar un texto =
 * crear otra plantilla y esperar la aprobación.
 */

export interface TwilioTemplate {
    sid: string;
    friendlyName: string;
    language: string;
    dateCreated?: string;
    body?: string;
    actions?: { title?: string; url?: string }[];
    variableCount: number;
    /** `undefined` = nunca se mandó a aprobar: existe pero no se puede usar. */
    status?: string;
    rejectionReason?: string;
    category?: string;
}

const ESTADOS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    approved: { label: 'Aprobada', color: 'success' },
    pending: { label: 'En revisión', color: 'warning' },
    received: { label: 'En revisión', color: 'warning' },
    rejected: { label: 'Rechazada', color: 'error' }
};

export const getEstado = (status?: string) =>
    ESTADOS[status || ''] || { label: status ? status : 'Sin enviar a aprobación', color: 'default' as const };

/**
 * Las plantillas que **Twilio Verify creó solo**, una por cada idioma que
 * soporta (71 el 2025-10-27, el día que se dio de alta el servicio Verify).
 * Su cuerpo es literalmente `{{1}}`: el hueco del código OTP.
 *
 * ⚠️ **No se tocan.** No son nuestras: las administra Verify y elige cuál usar
 * según el idioma del destinatario. Borrar la equivocada deja a alguien sin
 * poder iniciar sesión, y no ahorran nada — Twilio cobra por mensaje enviado,
 * no por plantilla guardada.
 *
 * ⚠️ **Y por eso hay que ESCONDERLAS del selector de vinculación**: tienen UNA
 * variable, igual que el aviso de reseña de Google, así que aparecerían como 71
 * opciones válidas para él. Una lista así solo sirve para equivocarse.
 */
export const isTwilioManagedTemplate = (friendlyName?: string): boolean =>
    (friendlyName || '').startsWith('verify_auto_created');

const WhatsappTemplateList: React.FunctionComponent = () => {
    const [templates, setTemplates] = useState<TwilioTemplate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [truncated, setTruncated] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await axiosPrivate.get('/twilio/templates/all');
            setTemplates(data?.templates || []);
            setTruncated(Boolean(data?.truncated));
        } catch (err: any) {
            setError(err?.response?.data?.error || 'No se pudieron leer las plantillas desde Twilio.');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        void cargar();
    }, [cargar]);

    const borrar = (template: TwilioTemplate) => {
        showIOSAlert({
            title: 'Borrar plantilla',
            message: `Se va a borrar «${template.friendlyName}» de Twilio.\n\nNo se puede deshacer: recuperarla significa crearla otra vez y esperar a que Meta la apruebe.`,
            buttons: [
                {
                    text: 'Borrar',
                    style: 'destructive',
                    onPress: async () => {
                        showLoader();
                        try {
                            await axiosPrivate.delete(`/twilio/templates/${template.sid}`);
                            showAlertMessage('Plantilla borrada');
                            await cargar();
                        } catch (err: any) {
                            // El server se niega si algún aviso la está usando:
                            // borrarla lo dejaría mudo.
                            showAlertError(err?.response?.data?.error || err);
                        }
                        hideLoader();
                    }
                },
                { text: 'Cancelar', onPress: () => {}, style: 'cancel' }
            ]
        });
    };

    // Las de Verify se apartan: son 71 de las 82 y ahogarían las propias.
    const propias = templates.filter((t) => !isTwilioManagedTemplate(t.friendlyName));
    const deVerify = templates.filter((t) => isTwilioManagedTemplate(t.friendlyName));

    if (loading) {
        return (
            <Stack height="100%" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Box height="100%" p={4} sx={{ overflowY: 'auto' }}>
            <Stack spacing={2} maxWidth={900}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Stack>
                        <Typography variant="h5" fontWeight={600}>
                            Plantillas de WhatsApp
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Las que existen en la cuenta de Twilio, con lo que dice Meta de cada una.
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Volver a leer de Twilio">
                            <IconButton onClick={cargar}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
                            Nueva plantilla
                        </Button>
                    </Stack>
                </Stack>

                <Alert severity="info">
                    Una plantilla nueva <strong>no se puede usar hasta que Meta la apruebe</strong> (suele tardar de
                    minutos a un par de días). Y una vez aprobada, <strong>su texto ya no se puede cambiar</strong>:
                    para corregir algo se crea otra.
                </Alert>

                {error && <Alert severity="warning">{error}</Alert>}
                {truncated && (
                    <Alert severity="warning">
                        Hay más plantillas de las que caben aquí. Se muestran las más recientes.
                    </Alert>
                )}

                {propias.map((template) => {
                    const estado = getEstado(template.status);
                    return (
                        <Stack key={template.sid} component={Card} boxShadow={2} borderRadius={3} p={3} spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                <Stack flex={1} minWidth={0}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {template.friendlyName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {template.language} · {template.variableCount} variable(s) · {template.sid}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip size="small" label={estado.label} color={estado.color} />
                                    <Tooltip title="Borrar">
                                        <IconButton onClick={() => borrar(template)}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>

                            {template.rejectionReason && (
                                <Alert severity="error">Meta la rechazó: {template.rejectionReason}</Alert>
                            )}

                            {template.body && (
                                <>
                                    <Divider />
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                        {template.body}
                                    </Typography>
                                </>
                            )}
                            {template.actions?.map((action, index) => (
                                <Typography key={index} variant="caption" color="text.secondary">
                                    Botón «{action.title}» → {action.url}
                                </Typography>
                            ))}
                        </Stack>
                    );
                })}

                {!propias.length && !error && (
                    <Typography variant="body2" color="text.secondary">
                        No hay plantillas propias en la cuenta.
                    </Typography>
                )}

                {deVerify.length > 0 && (
                    <Alert severity="info" icon={false}>
                        <Typography variant="body2">
                            Además hay <strong>{deVerify.length} plantillas que Twilio creó solas</strong> para el
                            código de verificación, una por cada idioma que soporta. No se administran desde aquí y
                            no cuestan nada: Twilio cobra por mensaje enviado, no por plantilla guardada. Borrarlas
                            podría dejar sin código a alguien, así que se dejan como están.
                        </Typography>
                    </Alert>
                )}
            </Stack>

            <ModalNewTemplate
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreated={() => {
                    setModalOpen(false);
                    void cargar();
                }}
            />
        </Box>
    );
};

export default WhatsappTemplateList;
